import { css } from '@emotion/react/macro';
import styled from '@emotion/styled/macro';
import { BaseButton, DataTable, forPhoneOnly, forTabletPortraitDown, TitleCard, Variables } from '@enter/core';
import { Icon } from '@mdi/react';

interface ISelectableProps {
    selectable: boolean;
}

interface IAlign {
    align?: 'left' | 'right';
}
interface IExpand {
    expand?: boolean;
}

const hoverable = css`
    &:hover {
        background-color: ${Variables.Colors.p50};
        circle.dot {
            fill: ${Variables.Colors.b300};
        }
        circle.open {
            fill: ${Variables.Colors.white};
            stroke: ${Variables.Colors.b300};
        }
        line.fg {
            stroke: ${Variables.Colors.b300};
        }
        line.bg {
            stroke: ${Variables.Colors.p50};
        }
    }
`;

const selectable = (props: ISelectableProps) => css`
    display: block;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    cursor: ${props.selectable ? 'pointer' : 'default'};
`;

export const SelectableCell = styled.div<ISelectableProps>`
    ${selectable};
    padding: 8px 4px;
    line-height: 24px;
    height: 40px;
`;

export const StyledTitleCard = styled(TitleCard)`
    & > section > section:first-of-type {
        display: none;
    }
`;

export const StyledHeader = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 24px;
`;

export const StyledRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 12px 0 0 0;
`;

export const StyledCol = styled.div`
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    margin: 12px 0 0 0;

    &:first-of-type {
        align-items: flex-start;
    }
    &:last-of-type {
        align-items: flex-end;
    }

    ${forTabletPortraitDown(`
        flex: 1 1;
    `)}
`;

export const Spacer = styled.span`
    flex-grow: 1;
`;

export const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    margin: 12px 0;
    min-height: 200px;

    & > div {
        overflow-y: hidden;
    }

    div[role='table'] > div:first-of-type {
        [role='columnheader'] {
            white-space: nowrap;
        }
    }

    div[role='row'] {
        ${hoverable}
    }

    div[role='columnheader'] {
        padding: 8px 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    div[role='cell'] {
        display: block;
        font-size: 15px;
        color: ${Variables.Colors.n70};
        padding: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const StyledSegment = styled(SelectableCell)`
    ${selectable};
    padding: 0;
`;

export const StyledEvent = styled.span`
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 0 4px;
    white-space: nowrap;

    &:not(:first-of-type) {
        margin-left: 4px;
    }
`;
export const StyledDescription = styled(SelectableCell)`
    color: ${Variables.Colors.n100};
    width: 100%;
`;
export const Description = styled.span``;
export const Expand = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    padding: 0;
`;
export const ExpandSegment = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 50px;
`;
export const ExpandDetail = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: space-around;
    flex-grow: 1;
    padding: 0 8px;
`;
export const Table = styled.table<IExpand>`
    white-space: normal;
    color: ${Variables.Colors.n100};
    padding-bottom: 16px;

    min-width: 40%;

    &:first-of-type {
        padding-top: 16px;
    }

    ${forPhoneOnly(`
        font-size: smaller;
    `)}
`;

export const TD = styled.td<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
`;

export const TH = styled.th<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
    vertical-align: top;
    font-weight: 500;
`;

export const TR = styled.tr`
    & ${TD}, & ${TH} {
        font-size: 16px;
        letter-spacing: 0.5px;
        padding: 4px 8px;

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const THead = styled.thead`
    ${TH} {
        font-size: 20px;
        padding-bottom: 16px;
    }
`;
export const TBody = styled.tbody``;
export const TFoot = styled.tfoot``;

export const StyledButton = styled(BaseButton)`
    flex-grow: 1;
    margin-bottom: 8px;
    &:not(:first-of-type) {
        margin-left: 8px;
    }
`;

export const StyledIcon = styled(Icon)`
    margin-top: 4px;
    margin-right: 16px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.b300};
    align-self: flex-end;
`;

export const StyledLightIcon = styled(Icon)`
    fill: white;
`;

export const StyledLink = styled.a`
    color: ${Variables.Colors.p300};
    text-decoration: underline;
`;

export const RowGroupWrapper = styled.div`
    text-align: center;
    background: ${Variables.Colors.n15_48};
    padding: 10px 0;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid ${Variables.Colors.n15};
    border-bottom: 1px solid ${Variables.Colors.n15};

    &:hover {
        background: ${Variables.Colors.n15_16};
    }
`;
